import axios from "axios";
import { getAuthHeaders } from "./AuthApi";

const API_BASE_URL = "https://consultoriodraarias.api.ikeytechnologies.com/ambar/";

// ─────────────────────────────────────────────────────────
// SALA DE ESPERA (sin autenticación)
// ─────────────────────────────────────────────────────────

/**
 * Obtiene el siguiente video a reproducir en ciclo.
 * @param {number|null} ultimoId - ID del último video reproducido. null para el primero.
 */
export const getSiguienteVideo = async (ultimoId = null) => {
  try {
    const params = ultimoId !== null ? { ultimo_id: ultimoId } : {};
    const response = await axios.get(`${API_BASE_URL}videos/siguiente/`, { params });
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) return null; // No hay videos activos
    console.error("Error obteniendo siguiente video:", error);
    throw error;
  }
};

/**
 * Obtiene un video específico por ID (acceso público).
 * @param {number} id
 */
export const getVideoEspecifico = async (id) => {
  try {
    const response = await axios.get(`${API_BASE_URL}videos/especifico/${id}/`);
    return response.data;
  } catch (error) {
    console.error("Error obteniendo video específico:", error);
    throw error;
  }
};

// ─────────────────────────────────────────────────────────
// ADMINISTRACIÓN (requiere token de administrador)
// ─────────────────────────────────────────────────────────

/**
 * Lista todos los videos (activos e inactivos).
 */
export const getVideos = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}videos/`, getAuthHeaders());
    return response.data;
  } catch (error) {
    console.error("Error listando videos:", error);
    throw error;
  }
};

/**
 * Sube un nuevo video publicitario.
 * @param {FormData} formData - Debe incluir 'video' (File), 'titulo' (string), opcionalmente 'orden'.
 * @param {function} onUploadProgress - Callback con el progreso (0-100).
 */
export const uploadVideo = async (formData, onUploadProgress) => {
  try {
    const { headers } = getAuthHeaders();
    const response = await axios.post(`${API_BASE_URL}videos/`, formData, {
      headers: {
        ...headers,
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        if (onUploadProgress && progressEvent.total) {
          const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onUploadProgress(pct);
        }
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error subiendo video:", error);
    throw error;
  }
};

/**
 * Edita el título, orden o estado activo de un video.
 * @param {number} id
 * @param {{ titulo?: string, orden?: number, activo?: boolean }} data
 */
export const updateVideo = async (id, data) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}videos/${id}/`,
      data,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    console.error("Error actualizando video:", error);
    throw error;
  }
};

/**
 * Elimina un video por ID.
 * @param {number} id
 */
export const deleteVideo = async (id) => {
  try {
    await axios.delete(`${API_BASE_URL}videos/${id}/`, getAuthHeaders());
    return true;
  } catch (error) {
    console.error("Error eliminando video:", error);
    throw error;
  }
};

/**
 * Reordena múltiples videos a la vez.
 * @param {Array<{ id: number, orden: number }>} videos
 */
export const reordenarVideos = async (videos) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}videos/reordenar/`,
      { videos },
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    console.error("Error reordenando videos:", error);
    throw error;
  }
};
